import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Undo2, History, Loader2, ChevronDown } from 'lucide-react';

/**
 * Undo / history toolbar for auto-saving forms.
 *
 * Backed by the unified `form_history` collection (scopes: 'components',
 * 'conditions') or the dedicated `pricing_history` collection (scope:
 * 'pricing'). The component fetches up to 20 most-recent entries for the
 * given appraisal+scope and exposes:
 *
 *   - One-click "Undo last save" button — restores the newest entry.
 *   - "History ▾" dropdown with the last 10 entries, each individually
 *     restorable.
 *
 * After a successful restore, fires a sonner toast with a `[Redo]` action
 * button. The redo simply restores the new "auto-snapshot before
 * restoring" entry that the backend wrote at the same moment, so Redo
 * is itself undoable — it's a full bidirectional history walk.
 *
 * Props:
 *   appraisalId   string  — required
 *   scope         'components' | 'conditions' | 'pricing'
 *   onRestored    () => void  — called after a successful restore so the
 *                               parent can reload its data.
 *   refreshKey    any  — bump this from the parent after every save to
 *                        force a history refetch.
 *   compact       bool — hide the labels, show icons only.
 *   testIdPrefix  string — prefix for data-testids (default 'undo')
 */
export default function UndoToolbar({
  appraisalId, scope, onRestored, refreshKey = 0, compact = false,
  testIdPrefix = 'undo',
}) {
  const { api } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [open, setOpen] = useState(false);

  const isPricing = scope === 'pricing';

  const load = useCallback(async () => {
    if (!appraisalId || !scope) return;
    setLoading(true);
    try {
      if (isPricing) {
        const { data } = await api.get(`/appraisals/${appraisalId}/pricing-history`);
        setEntries((data || []).map(e => ({
          _id: e._id || e.id, ts: e.ts, user_name: e.user_name || 'system',
          action: e.action || 'save',
          label: e.label || `Pricing — markup ${(e.markup ?? 1).toFixed(2)}x · grand $${(e.grand_total || 0).toLocaleString()}`,
        })));
      } else {
        const { data } = await api.get(`/appraisals/${appraisalId}/form-history?scope=${scope}`);
        setEntries(data?.entries || []);
      }
    } catch {
      setEntries([]);
    }
    setLoading(false);
  }, [api, appraisalId, scope, isPricing]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Close the dropdown when clicking outside.
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [open]);

  const restore = async (entry) => {
    setRestoring(entry._id);
    setOpen(false);
    try {
      const url = isPricing
        ? `/appraisals/${appraisalId}/pricing-history/${entry._id}/restore`
        : `/appraisals/${appraisalId}/form-history/${entry._id}/restore`;
      await api.post(url);
      // Show a persistent toast with a Redo affordance. duration:Infinity so
      // the user has unlimited time to click Redo even if they get distracted.
      toast.success(
        `Restored save from ${relTs(entry.ts)} by ${entry.user_name || 'system'}`,
        {
          description: 'Click Redo to come back to the state you were just in.',
          duration: Infinity,
          action: {
            label: 'Redo',
            onClick: async () => {
              // Refresh and grab the NEWEST entry (which the backend just
              // wrote as the "auto-snapshot before restore" — restoring it
              // returns us to the post-edit state).
              try {
                const fresh = isPricing
                  ? (await api.get(`/appraisals/${appraisalId}/pricing-history`)).data
                  : (await api.get(`/appraisals/${appraisalId}/form-history?scope=${scope}`)).data?.entries;
                const newestRaw = (fresh || [])[0];
                const newest = newestRaw && { _id: newestRaw._id || newestRaw.id };
                if (!newest) {
                  toast.error('Nothing to redo — history is empty.');
                  return;
                }
                const redoUrl = isPricing
                  ? `/appraisals/${appraisalId}/pricing-history/${newest._id}/restore`
                  : `/appraisals/${appraisalId}/form-history/${newest._id}/restore`;
                await api.post(redoUrl);
                toast.success('Redone.');
                if (onRestored) onRestored();
                load();
              } catch (err) {
                toast.error(err?.response?.data?.detail || 'Redo failed.');
              }
            },
          },
        }
      );
      if (onRestored) onRestored();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed.');
    }
    setRestoring(null);
  };

  const newest = entries[0];
  const hasHistory = entries.length > 0;

  if (loading && !hasHistory) {
    return (
      <div data-testid={`${testIdPrefix}-loading-${scope}`} className="flex items-center gap-1.5 text-[10px] text-slate-400">
        <Loader2 size={11} className="animate-spin" /> {compact ? '' : 'Loading history…'}
      </div>
    );
  }

  if (!hasHistory) {
    return (
      <span data-testid={`${testIdPrefix}-empty-${scope}`} className="text-[10px] text-slate-400">
        {compact ? '' : 'No saves yet to undo.'}
      </span>
    );
  }

  return (
    <div className="flex items-center gap-1.5 relative" onClick={e => e.stopPropagation()}>
      <button
        data-testid={`${testIdPrefix}-last-${scope}`}
        disabled={!!restoring}
        onClick={() => restore(newest)}
        title={`Undo last save — ${relTs(newest.ts)} by ${newest.user_name}`}
        className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-600 hover:text-white font-bold transition-colors disabled:opacity-40"
      >
        {restoring === newest._id ? <Loader2 size={11} className="animate-spin" /> : <Undo2 size={11} />}
        {compact ? '' : 'Undo last save'}
      </button>

      <button
        data-testid={`${testIdPrefix}-history-toggle-${scope}`}
        onClick={() => setOpen(o => !o)}
        title="Open save history"
        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100 font-semibold transition-colors"
      >
        <History size={11} /> {compact ? '' : `${entries.length}`} <ChevronDown size={10} />
      </button>

      {open && (
        <div data-testid={`${testIdPrefix}-history-dropdown-${scope}`} className="absolute z-50 top-full mt-1 right-0 w-80 max-h-96 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-xl">
          <div className="px-3 py-2 border-b border-slate-100 sticky top-0 bg-white">
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Save history · {scope}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Click any entry to restore that save.</p>
          </div>
          <ul className="divide-y divide-slate-50">
            {entries.slice(0, 10).map((e, i) => (
              <li key={e._id}>
                <button
                  data-testid={`${testIdPrefix}-history-entry-${i}-${scope}`}
                  disabled={!!restoring}
                  onClick={() => restore(e)}
                  className="w-full text-left px-3 py-2 hover:bg-amber-50/50 transition-colors disabled:opacity-40"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-bold text-slate-700">{relTs(e.ts)}</span>
                    <span className="text-[10px] text-slate-400 font-mono">{i === 0 ? 'latest' : `#${i + 1}`}</span>
                  </div>
                  <p className="text-[10px] text-slate-500 truncate mt-0.5">by {e.user_name || 'system'} · {e.action || 'save'}</p>
                  {e.label && <p className="text-[10px] text-slate-400 truncate mt-0.5">{e.label}</p>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function relTs(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  const diffSec = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return `${Math.round(diffSec)}s ago`;
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
  return d.toLocaleString('en-NZ', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}
